// frontend/src/pages/CamDoBrainTrends.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import camdobrainService from '../services/camdobrainService';
import Loading from '../components/Loading';
import './CamDoBrainTrends.css';

const CamDoBrainTrends = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const brandProfileIdParam = searchParams.get('brand_profile_id');

  // State
  const [loading, setLoading] = useState(true);
  const [brandProfiles, setBrandProfiles] = useState([]);
  const [selectedBrandId, setSelectedBrandId] = useState(null);
  const [activeTab, setActiveTab] = useState('home'); // home, explore, realtime
  const [selectedCountry, setSelectedCountry] = useState('CL');
  const [error, setError] = useState(null);

  // Google Trends Data
  const [metadata, setMetadata] = useState({ countries: [], categories: [], timeframes: [] });
  const [homeData, setHomeData] = useState(null);
  const [realtimeData, setRealtimeData] = useState([]);
  const [exploreData, setExploreData] = useState(null);

  // Explore State
  const [exploreKeyword, setExploreKeyword] = useState('');
  const [exploreTimeframe, setExploreTimeframe] = useState('today_3m');
  const [compareKeywords, setCompareKeywords] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [exploring, setExploring] = useState(false);

  // Load initial data
  useEffect(() => {
    loadInitialData();
  }, []);

  // Load trends when country changes
  useEffect(() => {
    if (activeTab === 'home') {
      loadHomeData();
    } else if (activeTab === 'realtime') {
      loadRealtimeData();
    }
  }, [selectedCountry, activeTab]);

  const loadInitialData = async () => {
    try {
      setLoading(true);

      // Load brand profiles and metadata in parallel
      const [profilesRes, metadataRes] = await Promise.all([
        camdobrainService.getBrandProfiles(),
        camdobrainService.getGoogleTrendsMetadata(),
      ]);

      const profiles = Array.isArray(profilesRes.data)
        ? profilesRes.data
        : (profilesRes.data?.results || []);
      setBrandProfiles(profiles);

      setMetadata(metadataRes.data);

      if (brandProfileIdParam && profiles.find(p => p.id === parseInt(brandProfileIdParam))) {
        setSelectedBrandId(parseInt(brandProfileIdParam));
      } else if (profiles.length > 0) {
        setSelectedBrandId(profiles[0].id);
      }

      // Load home data
      await loadHomeData();
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const loadHomeData = async () => {
    try {
      const res = await camdobrainService.getGoogleTrendsHome(selectedCountry);
      setHomeData(res.data);
    } catch (err) {
      console.error('Error loading home data:', err);
    }
  };

  const loadRealtimeData = async () => {
    try {
      setLoading(true);
      const res = await camdobrainService.getGoogleTrendsRealtime(selectedCountry);
      setRealtimeData(res.data.realtime_trends || []);
    } catch (err) {
      console.error('Error loading realtime data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleExplore = async (e) => {
    e?.preventDefault();
    if (!exploreKeyword.trim()) return;

    try {
      setExploring(true);
      setError(null);
      const res = await camdobrainService.exploreGoogleTrends(
        exploreKeyword,
        selectedCountry,
        exploreTimeframe,
        compareKeywords || null
      );
      setExploreData(res.data);
    } catch (err) {
      console.error('Error exploring:', err);
      setError('Error al explorar término');
    } finally {
      setExploring(false);
    }
  };

  const handleKeywordChange = async (value) => {
    setExploreKeyword(value);
    if (value.length >= 2) {
      try {
        const res = await camdobrainService.getGoogleTrendsSuggestions(value);
        setSuggestions(res.data.suggestions || []);
      } catch (err) {
        console.error('Error getting suggestions:', err);
      }
    } else {
      setSuggestions([]);
    }
  };

  const selectSuggestion = (suggestion) => {
    setExploreKeyword(suggestion.title);
    setSuggestions([]);
  };

  const getCountryName = (code) => {
    const country = metadata.countries.find(c => c.code === code);
    return country ? country.name : code;
  };

  if (loading && !homeData) {
    return <Loading />;
  }

  return (
    <div className="trends-page-camdobrain">
      {/* Header */}
      <div className="trends-header">
        <div className="header-left">
          <button className="back-btn" onClick={() => navigate('/camdobrain')}>
            ← Volver
          </button>
          <h1>Google Trends</h1>
          <span className="subtitle">Tendencias de búsqueda en tiempo real</span>
        </div>
        <div className="header-actions">
          <select
            className="country-select"
            value={selectedCountry}
            onChange={(e) => setSelectedCountry(e.target.value)}
          >
            {metadata.countries.map(country => (
              <option key={country.code} value={country.code}>
                {country.name}
              </option>
            ))}
          </select>
          <select
            className="brand-select"
            value={selectedBrandId || ''}
            onChange={(e) => setSelectedBrandId(parseInt(e.target.value))}
          >
            {brandProfiles.map(profile => (
              <option key={profile.id} value={profile.id}>
                {profile.brand_name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {error && (
        <div className="error-banner">{error}</div>
      )}

      {/* Tabs */}
      <div className="tabs-container">
        <button
          className={`tab ${activeTab === 'home' ? 'active' : ''}`}
          onClick={() => setActiveTab('home')}
        >
          🏠 Inicio
        </button>
        <button
          className={`tab ${activeTab === 'explore' ? 'active' : ''}`}
          onClick={() => setActiveTab('explore')}
        >
          🔍 Explorar
        </button>
        <button
          className={`tab ${activeTab === 'realtime' ? 'active' : ''}`}
          onClick={() => setActiveTab('realtime')}
        >
          📈 Tendencias Actuales
        </button>
      </div>

      {/* Content */}
      <div className="trends-content">
        {/* HOME TAB */}
        {activeTab === 'home' && homeData && (
          <div className="home-tab">
            <div className="section">
              <div className="section-header">
                <h2>🔥 Tendencias del momento en {getCountryName(selectedCountry)}</h2>
                <span className="last-updated">
                  Actualizado: {new Date(homeData.last_updated).toLocaleString('es-CL')}
                </span>
              </div>

              {homeData.trending_searches?.length > 0 ? (
                <div className="trending-grid">
                  {homeData.trending_searches.map((trend, index) => (
                    <div
                      key={index}
                      className={`trending-card ${trend.is_new ? 'is-new' : ''}`}
                      onClick={() => {
                        setExploreKeyword(trend.title);
                        setActiveTab('explore');
                        setTimeout(() => handleExplore(), 100);
                      }}
                    >
                      <div className="trend-rank">#{trend.rank}</div>
                      <div className="trend-info">
                        <h3 className="trend-title">{trend.title}</h3>
                        <span className={`trend-traffic traffic-${trend.traffic?.toLowerCase()}`}>
                          {trend.traffic}
                        </span>
                      </div>
                      {trend.is_new && <span className="new-badge">Nuevo</span>}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state">
                  <p>No hay tendencias disponibles para este país</p>
                </div>
              )}
            </div>

            {homeData.recent_trends?.length > 0 && (
              <div className="section">
                <h2>📰 Tendencias del día con contexto</h2>
                <div className="daily-trends-list">
                  {homeData.recent_trends.map((trend, index) => (
                    <div key={index} className="daily-trend-card">
                      <div className="daily-trend-header">
                        <h3>{trend.title}</h3>
                        {trend.traffic && trend.traffic !== 'N/A' && (
                          <span className="traffic-badge">{trend.traffic} búsquedas</span>
                        )}
                      </div>

                      {trend.related_queries?.length > 0 && (
                        <div className="related-queries">
                          <span className="label">Búsquedas relacionadas:</span>
                          <div className="query-tags">
                            {trend.related_queries.map((query, idx) => (
                              <span
                                key={idx}
                                className="query-tag"
                                onClick={() => {
                                  setExploreKeyword(query);
                                  setActiveTab('explore');
                                }}
                              >
                                {query}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {trend.articles?.length > 0 && (
                        <div className="related-articles">
                          <span className="label">Noticias relacionadas:</span>
                          {trend.articles.map((article, idx) => (
                            <a
                              key={idx}
                              href={article.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="article-link"
                            >
                              <span className="article-source">{article.source}</span>
                              <span className="article-title">{article.title}</span>
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* EXPLORE TAB */}
        {activeTab === 'explore' && (
          <div className="explore-tab">
            <div className="explore-form-section">
              <form onSubmit={handleExplore} className="explore-form">
                <div className="search-input-wrapper">
                  <input
                    type="text"
                    placeholder="Ingresa un término de búsqueda..."
                    value={exploreKeyword}
                    onChange={(e) => handleKeywordChange(e.target.value)}
                    className="explore-input"
                  />
                  {suggestions.length > 0 && (
                    <div className="suggestions-dropdown">
                      {suggestions.map((suggestion, idx) => (
                        <div
                          key={idx}
                          className="suggestion-item"
                          onClick={() => selectSuggestion(suggestion)}
                        >
                          <span className="suggestion-title">{suggestion.title}</span>
                          {suggestion.type && (
                            <span className="suggestion-type">{suggestion.type}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <select
                  value={exploreTimeframe}
                  onChange={(e) => setExploreTimeframe(e.target.value)}
                  className="timeframe-select"
                >
                  {metadata.timeframes.map(tf => (
                    <option key={tf.id} value={tf.id}>{tf.label}</option>
                  ))}
                </select>

                <input
                  type="text"
                  placeholder="Comparar con... (separado por comas)"
                  value={compareKeywords}
                  onChange={(e) => setCompareKeywords(e.target.value)}
                  className="compare-input"
                />

                <button type="submit" className="explore-btn" disabled={exploring}>
                  {exploring ? 'Buscando...' : 'Explorar'}
                </button>
              </form>
            </div>

            {exploreData && (
              <div className="explore-results">
                <div className="explore-header">
                  <h2>Resultados para "{exploreData.keyword}"</h2>
                  <div className="explore-meta">
                    <span>País: {getCountryName(exploreData.geo)}</span>
                    <span>Período: {exploreData.timeframe_label}</span>
                    {exploreData.current_interest > 0 && (
                      <span className="current-interest">
                        Interés actual: <strong>{exploreData.current_interest}</strong>
                      </span>
                    )}
                  </div>
                </div>

                {/* Interest Over Time Chart placeholder */}
                {exploreData.interest_over_time?.length > 0 && (
                  <div className="section chart-section">
                    <h3>📊 Interés a lo largo del tiempo</h3>
                    <div className="simple-chart">
                      {exploreData.interest_over_time.slice(-30).map((point, idx) => (
                        <div
                          key={idx}
                          className="chart-bar"
                          style={{
                            height: `${point.values[exploreData.keyword] || 0}%`,
                          }}
                          title={`${point.date}: ${point.values[exploreData.keyword] || 0}`}
                        />
                      ))}
                    </div>
                    <div className="chart-labels">
                      <span>{exploreData.interest_over_time[0]?.date}</span>
                      <span>{exploreData.interest_over_time[exploreData.interest_over_time.length - 1]?.date}</span>
                    </div>
                  </div>
                )}

                {/* Interest by Region */}
                {exploreData.interest_by_region?.length > 0 && (
                  <div className="section">
                    <h3>🌍 Interés por región</h3>
                    <div className="regions-list">
                      {exploreData.interest_by_region.slice(0, 10).map((region, idx) => (
                        <div key={idx} className="region-item">
                          <span className="region-name">{region.region}</span>
                          <div className="region-bar-wrapper">
                            <div
                              className="region-bar"
                              style={{ width: `${region.value}%` }}
                            />
                          </div>
                          <span className="region-value">{region.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Related Topics */}
                {(exploreData.related_topics?.top?.length > 0 || exploreData.related_topics?.rising?.length > 0) && (
                  <div className="section">
                    <h3>💡 Temas relacionados</h3>
                    <div className="related-grid">
                      {exploreData.related_topics?.rising?.length > 0 && (
                        <div className="related-column">
                          <h4>🚀 En ascenso</h4>
                          <div className="related-list">
                            {exploreData.related_topics.rising.map((topic, idx) => (
                              <div
                                key={idx}
                                className="related-item rising"
                                onClick={() => {
                                  setExploreKeyword(topic.title);
                                  handleExplore();
                                }}
                              >
                                <span className="item-title">{topic.title}</span>
                                <span className="item-value">{topic.value}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {exploreData.related_topics?.top?.length > 0 && (
                        <div className="related-column">
                          <h4>⭐ Principales</h4>
                          <div className="related-list">
                            {exploreData.related_topics.top.map((topic, idx) => (
                              <div
                                key={idx}
                                className="related-item"
                                onClick={() => {
                                  setExploreKeyword(topic.title);
                                  handleExplore();
                                }}
                              >
                                <span className="item-title">{topic.title}</span>
                                <span className="item-value">{topic.value}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Related Queries */}
                {(exploreData.related_queries?.top?.length > 0 || exploreData.related_queries?.rising?.length > 0) && (
                  <div className="section">
                    <h3>🔍 Consultas relacionadas</h3>
                    <div className="related-grid">
                      {exploreData.related_queries?.rising?.length > 0 && (
                        <div className="related-column">
                          <h4>🚀 En ascenso</h4>
                          <div className="related-list">
                            {exploreData.related_queries.rising.map((query, idx) => (
                              <div
                                key={idx}
                                className="related-item rising"
                                onClick={() => {
                                  setExploreKeyword(query.query);
                                  handleExplore();
                                }}
                              >
                                <span className="item-title">{query.query}</span>
                                <span className="item-value">{query.value}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {exploreData.related_queries?.top?.length > 0 && (
                        <div className="related-column">
                          <h4>⭐ Principales</h4>
                          <div className="related-list">
                            {exploreData.related_queries.top.map((query, idx) => (
                              <div
                                key={idx}
                                className="related-item"
                                onClick={() => {
                                  setExploreKeyword(query.query);
                                  handleExplore();
                                }}
                              >
                                <span className="item-title">{query.query}</span>
                                <span className="item-value">{query.value}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {!exploreData && (
              <div className="explore-empty">
                <div className="empty-icon">🔍</div>
                <h2>Explora tendencias de búsqueda</h2>
                <p>Ingresa un término para ver su popularidad a lo largo del tiempo, interés por región y consultas relacionadas.</p>
              </div>
            )}
          </div>
        )}

        {/* REALTIME TAB */}
        {activeTab === 'realtime' && (
          <div className="realtime-tab">
            <div className="section">
              <div className="section-header">
                <h2>📈 Tendencias actuales en {getCountryName(selectedCountry)}</h2>
                <button
                  className="refresh-btn"
                  onClick={loadRealtimeData}
                  disabled={loading}
                >
                  {loading ? 'Actualizando...' : '🔄 Actualizar'}
                </button>
              </div>

              {loading ? (
                <Loading mensaje="Cargando tendencias" fullScreen={false} />
              ) : realtimeData.length > 0 ? (
                <div className="realtime-list">
                  {realtimeData.map((trend, index) => (
                    <div key={index} className="realtime-card">
                      <div className="realtime-header">
                        <h3>{trend.title}</h3>
                        {trend.traffic && trend.traffic !== 'N/A' && (
                          <span className="traffic-badge">{trend.traffic}</span>
                        )}
                      </div>

                      {trend.entity_names?.length > 0 && (
                        <div className="entity-tags">
                          {trend.entity_names.map((entity, idx) => (
                            <span key={idx} className="entity-tag">{entity}</span>
                          ))}
                        </div>
                      )}

                      {trend.articles?.length > 0 && (
                        <div className="realtime-articles">
                          {trend.articles.map((article, idx) => (
                            <a
                              key={idx}
                              href={article.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="realtime-article"
                            >
                              <div className="article-header">
                                <span className="article-source">{article.source}</span>
                                {article.time && (
                                  <span className="article-time">{article.time}</span>
                                )}
                              </div>
                              <p className="article-title">{article.title}</p>
                              {article.snippet && (
                                <p className="article-snippet">{article.snippet}</p>
                              )}
                            </a>
                          ))}
                        </div>
                      )}

                      {trend.related_queries?.length > 0 && (
                        <div className="realtime-queries">
                          <span className="label">Relacionado:</span>
                          {trend.related_queries.map((query, idx) => (
                            <span
                              key={idx}
                              className="query-tag"
                              onClick={() => {
                                setExploreKeyword(query);
                                setActiveTab('explore');
                              }}
                            >
                              {query}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state">
                  <div className="empty-icon">📈</div>
                  <h2>No hay tendencias en tiempo real disponibles</h2>
                  <p>Las tendencias en tiempo real pueden no estar disponibles para todos los países.</p>
                  <p>Intenta con otro país o revisa la pestaña "Inicio" para ver las tendencias del día.</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CamDoBrainTrends;
